import { Button } from "@chakra-ui/button";
import { Box, Flex, Icon, TableIconButton, Td, Text, Tr, useToast, useTranslation } from "@familyzone/component-library";
import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import { displayTypesArray, ObjectPool } from "../../../types/Objects";
import useIsMounted from "../../../utils/hooks/useIsMounted";
import TableBasedPage from "../../templates/TableBasedPage";
import DeleteObjectPoolsModal from "./DeleteObjectPoolsModal";
import NewAddObjectPools from "./AddObjectPools";
import PropTypes from "prop-types";
import { TableColumn } from "../../../types/table";
import { CaptureYoutubeObjectListCreated } from "../../../utils/PostHogUtils";
import { RouterContext } from "../../../utils/RouterContext";
import { useObjectStore } from "../../../storez/ObjectStore";

const ObjectPools: React.FC = (_, context: RouterContext) => {
  const { errorToast } = useToast();
  const isMounted = useIsMounted();
  const [objects, setObjects] = useState<ObjectPool[]>([]);
  const [loaded, setLoaded] = useState(true);
  const [openNewObject, setOpenNewObject] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingObject, setEditingObject] = useState<ObjectPool | null>(null);
  const [openDeleteObject, setOpenDeleteObject] = useState(false);

  const { t } = useTranslation();

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config/device/dashboard", isActive: false },
    { title: t("Objects"), url: "/config/device/objects/pools", isActive: false },
    { title: t("Pools"), isActive: true },
  ];

  const { fetch, getLoaded, getObjects, addObject, deleteObject, saveObject } = useObjectStore();

  const onChange = () => {
    setLoaded(false);
    if (getLoaded()) {
      setObjects(
        getObjects().map((object) => ({
          ...object,
          displayType: displayTypesArray.find((displayType) => displayType[0] === object.type)?.[1],
        }))
      );
    }
    setLoaded(true);
  };

  useEffect(() => {
    const unsubscribe = useObjectStore.subscribe(onChange);

    void (async () => {
      setLoaded(false);
      await fetch();
      onChange();
      setLoaded(true);
    })();

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: TableColumn[] = [
    {
      headerText: t("Name"),
      columnName: "name",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Description"),
      columnName: "desc",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Type"),
      columnName: "displayType",
      sortable: true,
      searchable: true,
    },
    {
      headerText: t("Operations"),
      columnName: "operations",
      alignment: "right",
    },
  ];

  const handleDelete = (id: string) => {
    if (!isMounted) return;
    setLoaded(false);
    void deleteObject(id);
    setOpenDeleteObject(false);
    onChange();
    setLoaded(true);
  };

  const handleAddObject = async (object: ObjectPool) => {
    if (!isMounted) return;
    setLoaded(false);
    if (editing) {
      try {
        await saveObject(object);
      } catch (e) {
        setLoaded(true);
        errorToast({ title: "Failed to update object List", description: "Please try again later." });
      }
    } else {
      const result = await addObject(object);
      if (result.result.id) {
        context.router.push(`/config/device/objects/pools/${result.result.id}`);

        CaptureYoutubeObjectListCreated(result.result);
      }
    }
    onChange();
    setOpenNewObject(false);
    setLoaded(true);
  };

  const handleOpenNewObject = () => {
    setOpenNewObject(true);
    setEditing(false);
  };

  const handleCloseAddObject = () => {
    setOpenNewObject(false);
    setEditing(false);
    setEditingObject(null);
  };

  const handleOpenEditObject = (object: ObjectPool) => {
    setOpenNewObject(true);
    setEditing(true);
    setEditingObject(object);
  };

  const handleCloseDeleteConfirmation = () => {
    setOpenDeleteObject(false);
    setEditing(false);
    setEditingObject(null);
  };

  const handleOpenDeleteConfirmation = (object: ObjectPool) => {
    setOpenDeleteObject(true);
    setEditing(true);
    setEditingObject(object);
  };

  const tableDataMap = (row: ObjectPool, index: number): JSX.Element => (
    <Tr key={index} role="row">
      <Td role="cell">
        {row.id ? (
          <Link to={`/config/device/objects/pools/${encodeURIComponent(row.id)}`} aria-label={`View Object List ${row.name}`}>
            <Text color="var(--chakra-colors-brand-500)" fontSize="md" minWidth="200px">
              {row?.name ? row.name : "___"}
            </Text>
          </Link>
        ) : (
          ""
        )}
      </Td>
      <Td role="cell">
        <Text fontSize="md" aria-label={row.desc}>
          {row.desc}
        </Text>
      </Td>
      <Td role="cell">
        <Text fontSize="md" aria-label={row.displayType}>
          {row.displayType}
        </Text>
      </Td>
      <Td role="cell">
        <Flex justifyContent="end">
          <Box mr="sp8">
            <TableIconButton
              onClick={() => handleOpenEditObject(row)}
              icon={<Icon icon="fa-pencil" variant="solid" color="text.paragraph.light" />}
              aria-label={"Edit association"}
              data-testid={row.id ? `${row.id}-edit` : "edit"}
            />
          </Box>
          <TableIconButton
            icon={<Icon icon="fa-trash-can" variant="solid" color="text.paragraph.light" />}
            aria-label={"Delete association"}
            onClick={() => handleOpenDeleteConfirmation(row)}
            data-testid={row.id ? `${row.id}-delete` : "delete"}
          />
        </Flex>
      </Td>
    </Tr>
  );

  return (
    <>
      <TableBasedPage
        title={t("Pools")}
        breadcrumbs={breadcrumbs}
        columns={columns}
        data={objects}
        tableDataMap={tableDataMap}
        loaded={loaded}
        showSearch={true}
        childrenInTableHeader={
          <Flex justifyContent="end">
            <Box>
              <Button onClick={handleOpenNewObject} variant="primary" aria-label="Add new object pool">
                {t("Add New Pool")}
              </Button>
            </Box>
          </Flex>
        }
      />
      <NewAddObjectPools
        open={openNewObject}
        editing={editing}
        objectPool={editingObject}
        onSave={(object: ObjectPool) => void handleAddObject(object)}
        loaded={loaded}
        onClose={handleCloseAddObject}
      />
      <DeleteObjectPoolsModal
        open={openDeleteObject}
        onClose={handleCloseDeleteConfirmation}
        id={editingObject?.id}
        type="objectpool"
        loaded={loaded}
        onDelete={handleDelete}
      />
    </>
  );
};
export default ObjectPools;

ObjectPools.contextTypes = {
  router: PropTypes.object.isRequired,
};
